import { ref, shallowRef } from 'vue';
import { contentDb } from '@/db/content';
import { pageItemsFromConsolidatedRows } from '@/stores/content/cache';
import { expandListingItemsWithLocaleVariants, getRootPathsAndDefaultLocale } from '@/navTree';
import {
  groupRankedPageMatches,
  rankPageMatches,
} from '@/lib/localPageSearch';
import type { PageItem } from '@/db/content';
import type { LocalPageSearchGroup } from '@/lib/localPageSearch';

export type { LocalPageSearchGroup, LocalPageSearchTranslation } from '@/lib/localPageSearch';

const SEARCH_DEBOUNCE_MS = 180;

export function useLocalPageSearch() {
  const hits = shallowRef<LocalPageSearchGroup[]>([]);
  const isSearching = ref(false);
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;
  let requestSerial = 0;

  function clearDebounced(): void {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer);
      debounceTimer = undefined;
    }
  }

  async function runSearch(trimmedQuery: string, uiLocale: string): Promise<void> {
    const serial = ++requestSerial;
    if (!trimmedQuery) {
      hits.value = [];
      isSearching.value = false;
      return;
    }

    try {
      const rows = await contentDb.page.toArray();
      if (serial !== requestSerial) return;

      const items: PageItem[] = expandListingItemsWithLocaleVariants(
        pageItemsFromConsolidatedRows(rows)
      );
      if (items.length === 0) {
        hits.value = [];
        return;
      }

      const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
      const ranked = rankPageMatches(items, trimmedQuery);
      const groups = groupRankedPageMatches(ranked, rootPathByLocale, defaultLocale, uiLocale);
      if (serial !== requestSerial) return;
      hits.value = groups;
    } catch {
      if (serial === requestSerial) hits.value = [];
    } finally {
      if (serial === requestSerial) isSearching.value = false;
    }
  }

  /** Debounced search from raw input (may include spaces). */
  function scheduleSearch(rawQuery: string, uiLocale: string): void {
    clearDebounced();
    const trimmed = rawQuery.trim();
    if (!trimmed) {
      requestSerial++;
      hits.value = [];
      isSearching.value = false;
      return;
    }

    isSearching.value = true;
    hits.value = [];
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined;
      void runSearch(trimmed, uiLocale);
    }, SEARCH_DEBOUNCE_MS);
  }

  function cancelAndClear(): void {
    clearDebounced();
    requestSerial++;
    hits.value = [];
    isSearching.value = false;
  }

  return {
    hits,
    isSearching,
    scheduleSearch,
    cancelAndClear,
  };
}
